import { useEffect, type ReactNode } from 'react'

interface PlatformPageProps {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}

/**
 * Page container used inside PlatformAdminShell. Mirrors the legacy
 * platform_admin.html main layout (h2 title + subtitle + content).
 */
export function PlatformPage({ title, description, actions, children }: PlatformPageProps) {
  useEffect(() => {
    const prev = document.title
    document.title = `${title} · Bokapsys Platform Admin`
    return () => {
      document.title = prev
    }
  }, [title])

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="m-0 text-[26px] font-bold leading-tight tracking-tight text-[#1a1a2e]">
            {title}
          </h2>
          {description ? (
            <p className="mt-1.5 max-w-3xl text-sm text-slate-500">{description}</p>
          ) : null}
        </div>
        {actions ? (
          // Same responsive pattern as components/Page.tsx: actions
          // wrap to a new line when there are too many to fit. Drops
          // flex-shrink-0 + adds flex-wrap + min-w-0 so 8+ buttons
          // never overflow the viewport.
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2 sm:basis-auto">
            {actions}
          </div>
        ) : null}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  )
}
